import { faPlus, faTrash, faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

import Button from '../../../../core/components/Button.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import Chip from '../config/Chip.tsx';
import { MAX_KNOWLEDGE_URLS, isValidKnowledgeUrl } from '../config/ConfigSteps.tsx';

const AddIcon = withIcon(faPlus);
const RemoveIcon = withIcon(faTrash);
const SyncIcon = withIcon(faCloudArrowDown);

export interface KnowledgeUrlStatus {
    url: string;
    status: 'synced' | 'failed' | 'pending';
    error: string | null;
    syncedAt: string | null;
}

export interface UrlSyncedFile {
    fileName: string;
    fileUrl: string;
}

const URL_STATUS_CHIP = {
    synced: { variant: 'success', labelKey: 'form.urlStatusSynced' },
    failed: { variant: 'danger', labelKey: 'form.urlStatusFailed' },
    pending: { variant: 'muted', labelKey: 'form.urlStatusPending' },
} as const;

const UrlCard = ({
    url,
    status,
    file,
    onRemove,
}: {
    url: string;
    status?: KnowledgeUrlStatus;
    file?: UrlSyncedFile;
    onRemove: () => void;
}) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const chip = URL_STATUS_CHIP[status?.status || 'pending'];

    return (
        <div className="w-full flex justify-between items-center border border-solid border-line-weak rounded p-3 shadow-sm text-content-strong">
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                    {file ? (
                        <a
                            href={file.fileUrl}
                            className="text-sm font-medium"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {file.fileName}
                        </a>
                    ) : (
                        <span className="text-sm font-medium break-all">{url}</span>
                    )}
                    <Chip>{t('form.sourceUrl')}</Chip>
                    <Chip variant={chip.variant}>{t(chip.labelKey)}</Chip>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-content-medium">
                    <SyncIcon />
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-content-medium underline break-all"
                    >
                        {url}
                    </a>
                    {status?.status === 'failed' && status.error ? (
                        <span className="text-content-danger-strong">&middot; {status.error}</span>
                    ) : (
                        status?.syncedAt && (
                            <span className="text-content-weak whitespace-nowrap">
                                &middot;{' '}
                                {t('form.lastSynced', {
                                    time: formatDistanceToNow(new Date(status.syncedAt), { addSuffix: true }),
                                })}
                            </span>
                        )
                    )}
                </div>
            </div>
            <Button
                variant="ghost"
                onClick={onRemove}
                className="text-content-danger-strong"
                type="button"
                aria-label={t('form.removeUrl')}
            >
                <RemoveIcon />
            </Button>
        </div>
    );
};

export const KnowledgeUrlItems = ({
    urls,
    onChange,
    savedUrls,
    statuses,
    syncedFiles,
}: {
    urls: string[];
    onChange: (urls: string[]) => void;
    savedUrls: Set<string>;
    statuses: Map<string, KnowledgeUrlStatus>;
    syncedFiles: Map<string, UrlSyncedFile>;
}) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    return (
        <>
            {urls.map((url, index) => {
                if (savedUrls.has(url.trim())) {
                    return (
                        <UrlCard
                            key={index}
                            url={url.trim()}
                            status={statuses.get(url.trim())}
                            file={syncedFiles.get(url.trim())}
                            onRemove={() => onChange(urls.filter((_, i) => i !== index))}
                        />
                    );
                }
                return (
                    <div key={index} className="flex flex-col gap-1">
                        <div className="flex gap-2 items-center">
                            <div className="grow">
                                <StringField
                                    value={url}
                                    onValueChange={(value) => onChange(urls.map((u, i) => (i === index ? value : u)))}
                                    placeholder={t('form.knowledgeUrlPlaceholder')}
                                />
                            </div>
                            <Button
                                variant="ghost"
                                type="button"
                                className="text-content-danger-strong"
                                onClick={() => onChange(urls.filter((_, i) => i !== index))}
                                aria-label={t('form.removeUrl')}
                            >
                                <RemoveIcon />
                            </Button>
                        </div>
                        {url.trim() !== '' && !isValidKnowledgeUrl(url) && (
                            <span className="text-xs text-content-danger-strong">{t('form.knowledgeUrlInvalid')}</span>
                        )}
                    </div>
                );
            })}
            {urls.length < MAX_KNOWLEDGE_URLS && (
                <Button
                    variant="outline"
                    type="button"
                    onClick={() => onChange([...urls, ''])}
                    className="flex w-full items-center justify-center gap-2"
                >
                    <AddIcon className="text-xs" />
                    {t('form.knowledgeUrlsAdd')}
                </Button>
            )}
        </>
    );
};
